import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, useTheme } from '@mui/material';
import { IMAGES } from '../../constants/assets';
import { validateFiles, FileValidationResult } from '../../utils/fileValidation';
import { getDragDropStyles } from '../../styles/theme';

interface FileUploaderProps {
  onFileSelect: (files: File[]) => void;
  accept?: Record<string, string[]>;
  maxSize?: number;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  multiple?: boolean;
  /** Wenn true darf der Nutzer einen kompletten Ordner auswählen (input.webkitdirectory) */
  directory?: boolean;
  /** Callback für Validierungsfehler */
  onValidationError?: (error: string, rejectedFiles?: File[]) => void;
  /** Spezielle Validierungstyp */
  validationType?: 'documents' | 'zip' | 'main-upload' | 'custom';
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileSelect,
  accept = { 
    'application/pdf': ['.pdf'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'text/plain': ['.txt']
  },
  maxSize = 10485760, // 10MB
  title = 'Dateien zum Hochladen hineinziehen oder',
  subtitle = 'PDF und DOCX-Dateien bis 10MB',
  buttonText = 'Auswählen',
  multiple = true,
  directory = false,
  onValidationError,
  validationType = 'documents'
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const dragDropStyles = getDragDropStyles(isDark);
  
  const validateAndProcessFiles = async (files: File[]): Promise<void> => {
    try {
      let validationResult: FileValidationResult;
      
      // 🔒 SICHERE VALIDIERUNG basierend auf Upload-Typ
      switch (validationType) {
        case 'documents':
          const { validateDocuments } = await import('../../utils/fileValidation');
          validationResult = await validateDocuments(files);
          break;
          
        case 'zip':
          if (files.length !== 1) {
            onValidationError?.('Bitte wählen Sie genau eine ZIP-Datei aus.', files);
            return;
          }
          const { validateZipFile } = await import('../../utils/fileValidation');
          validationResult = await validateZipFile(files[0]);
          break;
          
        case 'main-upload':
          if (files.length !== 1) {
            onValidationError?.('Bitte wählen Sie genau eine ZIP-Datei aus.', files);
            return;
          }
          const { validateMainUpload } = await import('../../utils/fileValidation');
          validationResult = await validateMainUpload(files[0]);
          break;
          
        case 'custom':
        default:
          // Fallback zu allgemeiner Validierung
          validationResult = await validateFiles(files, maxSize);
          break;
      }
      
      if (validationResult.isValid) {
        // ✅ Alle Dateien sind sicher
        onFileSelect(files);
      } else {
        // ❌ Sicherheitsproblem erkannt
        console.warn('🔒 Datei-Validierung fehlgeschlagen:', validationResult.error);
        onValidationError?.(
          validationResult.error || 'Unbekannter Validierungsfehler',
          validationResult.rejectedFiles
        );
      }
    } catch (error) {
      console.error('🔒 Fehler bei Datei-Validierung:', error);
      onValidationError?.(
        'Fehler bei der Sicherheitsvalidierung der Dateien.',
        files
      );
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[], rejectedFiles: any[]) => {
    // Prüfe zuerst auf dropzone-Ablehnungen
    if (rejectedFiles.length > 0) {
      const rejectedNames = rejectedFiles.map(f => f.file?.name || 'Unbekannt').join(', ');
      onValidationError?.(
        `Dateien abgelehnt: ${rejectedNames}. Bitte prüfen Sie Dateityp und -größe.`,
        rejectedFiles.map(f => f.file).filter(Boolean)
      );
    }
    
    if (acceptedFiles.length > 0) {
      // 🔒 SICHERE VALIDIERUNG
      await validateAndProcessFiles(acceptedFiles);
    }
  }, [onFileSelect, onValidationError, maxSize, validationType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple,
    noClick: true,
  });

  const handleFileSelect = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    if (directory) {
      // @ts-ignore webkitdirectory ist ein nicht standardisiertes Attribut, wird aber von Chromium-Browsern unterstützt
      input.webkitdirectory = true;
      // Für Ordner-Uploads brauchen wir Multiple immer true, sonst kommt nur ein Eintrag rein
      input.multiple = true;
    } else {
    input.multiple = multiple;
    }
    input.accept = Object.entries(accept)
      .map(([mime, exts]) => exts.map(ext => mime).join(','))
      .join(',');
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        // 🔒 SICHERE VALIDIERUNG
        await validateAndProcessFiles(Array.from(files));
      }
    };
    input.click();
  };

  return (
    <Box
      {...getRootProps()}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        backgroundColor: isDark ? '#1a2744' : '#F5F5F5',
        borderRadius: 1,
        transition: 'all 0.3s ease',
        height: '380px',
        width: '700px',
        boxShadow: 'none',
        overflow: 'hidden',
        ...dragDropStyles.container(
          isDragActive,
          directory 
            ? dragDropStyles.messages.folders
            : validationType === 'zip' || validationType === 'main-upload' 
              ? dragDropStyles.messages.zipOnly
              : validationType === 'documents'
                ? dragDropStyles.messages.documentsOnly
                : dragDropStyles.messages.generic
        ),
      }}
    >
      <input data-testid="provider-detail-upload-drop-zone" {...getInputProps()} />
      <img src={IMAGES.UPLOAD} alt="Upload" style={{ width: '40px', height: '40px', opacity: isDark ? 0.8 : 1, filter: isDark ? 'invert(1)' : 'none' }} />
      <Typography variant="h6" gutterBottom sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'text.gray', mt: 2 }}>
        {title}{' '}
        <Typography
          component="span"
          variant="h6"
          onClick={handleFileSelect}
          data-testid="file-uploader-select-button"
          sx={{ 
            color: isDark ? '#6b8cff' : 'primary.blue',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {buttonText}
        </Typography>
      </Typography>
      <Typography variant="body2" sx={{ color: isDark ? 'rgba(255,255,255,0.5)' : 'text.secondary' }}>
        {subtitle}
      </Typography>
    </Box>
  );
};

export default FileUploader; 